import { Section } from "@/components/ds/Section/Section";
import { Container } from "@/components/ds/Container/Container";
import { Stack } from "@/components/ds/Stack/Stack";
import { Row } from "@/components/ds/Row/Row";
import { Heading } from "@/components/ds/Heading/Heading";
import { Text } from "@/components/ds/Text/Text";
import { Reveal } from "@/components/ds/Reveal/Reveal";
import { CheckIcon } from "@/icons/CheckIcon";
import { MockRota } from "./MockRota";
import { MockCheckin } from "./MockCheckin";
import { MockDashboard } from "./MockDashboard";
import styles from "./HowItWorks.module.css";

interface StepFeature {
  readonly text: string;
}

interface Step {
  readonly number: string;
  readonly title: string;
  readonly description: string;
  readonly features: readonly StepFeature[];
  readonly mockup: React.ReactNode;
  readonly reverse?: boolean;
}

const STEPS: readonly Step[] = [
  {
    number: "01",
    title: "Schedule",
    description:
      "Build the week's rota in minutes. Drag shifts into slots, resize them to change hours, copy last week forward when Monday repeats itself.",
    features: [
      { text: "Drag-and-drop weekly grid, resize to change hours" },
      { text: "Copy last week forward, tweak only what's different" },
      { text: "Clash warnings flag double-bookings" },
      { text: "Publish the whole week in one click" },
    ],
    mockup: <MockRota />,
  },
  {
    number: "02",
    title: "Check in",
    description:
      "Cleaners open TidyRota in their browser and tap Check In. GPS confirms they're at the site, time-stamped, on the record.",
    features: [
      { text: "Opens in any phone browser, no app to install" },
      { text: "GPS confirms they're inside the site's radius" },
      { text: "Timestamped entry and exit on every shift" },
      { text: "Works even when GPS fails, flagged for you to review" },
    ],
    mockup: <MockCheckin />,
    reverse: true,
  },
  {
    number: "03",
    title: "See everything",
    description:
      "Your dashboard shows every site, every shift, every check-in. Missed check-ins float to the top so you spot them first.",
    features: [
      { text: "Live dashboard, refreshes every 30 seconds" },
      { text: "Shifts sorted by urgency" },
      { text: "Full attendance log, filter by date, site, or cleaner" },
      { text: "Check-in and check-out times on record for every visit" },
    ],
    mockup: <MockDashboard />,
  },
];

export function HowItWorks() {
  return (
    <Section background="cream">
      <Container>
        <Stack gap={10} align="center">
          <Stack gap={4} align="center">
            <Heading level={2} size="h2">
              How it works
            </Heading>
            <Text size="lg" color="primary">
              Three steps to run your cleaning operation with confidence.
            </Text>
          </Stack>

          {STEPS.map((step) => (
            <Reveal key={step.number}>
              <div
                className={`${styles.step} ${step.reverse ? styles.reverse : ""}`}
              >
                <div className={styles.stepContent}>
                  <Stack gap={5}>
                    <Text
                      as="span"
                      size="xs"
                      weight="semibold"
                      color="emerald"
                      className={styles.stepNumber}
                    >
                      STEP {step.number}
                    </Text>
                    <Heading level={3} size="h3">
                      {step.title}
                    </Heading>
                    <Text size="lg" color="primary">
                      {step.description}
                    </Text>
                    <Stack gap={3}>
                      {step.features.map((feature) => (
                        <Row key={feature.text} gap={3} wrap={false}>
                          <CheckIcon
                            width={18}
                            height={18}
                            className={styles.checkIcon}
                          />
                          <Text color="primary">{feature.text}</Text>
                        </Row>
                      ))}
                    </Stack>
                  </Stack>
                </div>
                <div className={styles.stepMockup}>{step.mockup}</div>
              </div>
            </Reveal>
          ))}
        </Stack>
      </Container>
    </Section>
  );
}
